import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { MapPin, ArrowDown } from "lucide-react";
import { scrollToId, EASE } from "@/lib/scroll";
import { STUDIO } from "@/lib/studio";
import { GiftNote } from "./GiftNote";

const LINES = [
  { t: "Schönheit, Pflege" },
  { t: "und Wohlbefinden –" },
  { t: "ganz persönlich", accent: true },
  { t: "für dich." },
];

const fade = (delay) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, ease: EASE, delay },
});

const RotatingBadge = () => (
  <div className="absolute -left-2 bottom-10 z-20 h-32 w-32 sm:-left-12 sm:h-40 sm:w-40">
    <svg viewBox="0 0 200 200" className="spin-slow absolute inset-0 h-full w-full" aria-hidden="true">
      <defs>
        <path id="badge-circle" d="M100,100 m-78,0 a78,78 0 1,1 156,0 a78,78 0 1,1 -156,0" />
      </defs>
      <circle cx="100" cy="100" r="98" fill="#FFFDF9" />
      <text className="fill-gold-deep" style={{ fontSize: 14, letterSpacing: 3 }}>
        <textPath href="#badge-circle" textLength="486" lengthAdjust="spacing">WEIL JEDER SCHRITT ZÄHLT ✦ BENSHEIM ✦</textPath>
      </text>
    </svg>
    <img src="/img/logo.png" alt="" className="absolute left-1/2 top-1/2 w-[58%] -translate-x-1/2 -translate-y-1/2" />
  </div>
);

const HeroVisual = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 80, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <div ref={ref} className="relative mx-auto w-full max-w-[460px] [perspective:1200px]" onMouseMove={onMove} onMouseLeave={reset}>
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative [transform-style:preserve-3d]">
        <div className="absolute -right-4 -top-4 h-full w-full rounded-t-full border border-gold/50 sm:-right-6 sm:-top-6" />
        <motion.div
          initial={{ clipPath: "inset(100% 0% 0% 0% round 999px 999px 0 0)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0% round 999px 999px 0 0)" }}
          transition={{ duration: 1.6, ease: EASE, delay: 0.2 }}
          className="relative h-[420px] overflow-hidden rounded-t-full bg-champagne sm:h-[560px] lg:h-[min(72vh,620px)]"
        >
          <motion.img
            style={{ y: imgY }}
            initial={{ scale: 1.25 }}
            animate={{ scale: 1.1 }}
            transition={{ duration: 2.2, ease: EASE, delay: 0.2 }}
            src="/img/atmosphere.jpg"
            alt="Sanfte Massage auf weißem Handtuch im Maison Princess"
            className="h-full w-full object-cover"
            fetchpriority="high"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/25 via-transparent to-transparent" />
        </motion.div>
        <motion.div {...fade(1.1)} style={{ translateZ: 60 }}>
          <RotatingBadge />
        </motion.div>
        <motion.p
          {...fade(1.3)}
          className="absolute bottom-6 right-6 z-20 font-script text-3xl text-cream drop-shadow sm:text-4xl"
        >
          Für dich gemacht.
        </motion.p>
      </motion.div>
    </div>
  );
};

export const Hero = () => (
  <section id="start" data-testid="hero-section" className="hero-glow relative overflow-hidden pt-20 sm:pt-28 lg:pt-32">
    <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 pb-12 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:gap-16 lg:pb-16">
      <div>
        <motion.div {...fade(0.05)} className="flex items-center gap-4">
          <img src="/img/logo.png" alt="Maison Princess Logo" className="float-soft w-20 sm:w-24" width="96" height="78" />
          <div>
            <p className="whitespace-nowrap font-serif text-lg tracking-[0.22em] text-ink sm:text-2xl sm:tracking-[0.3em]" data-testid="hero-brand">MAISON PRINCESS</p>
            <p className="mt-1 whitespace-nowrap text-[10px] uppercase tracking-[0.2em] text-gold-deep sm:text-[11px] sm:tracking-[0.34em]">Beauty · Wellness · Selfcare</p>
          </div>
        </motion.div>

        <h1 data-testid="hero-title" className="mt-7 font-serif text-[2.3rem] font-medium leading-[1.04] tracking-[-0.01em] text-ink sm:mt-10 sm:text-6xl lg:text-[4.3rem]">
          {LINES.map((l, i) => (
            <span key={l.t} className="block overflow-hidden pb-[0.06em]">
              <motion.span
                className={`block ${l.accent ? "italic text-gold-deep" : ""}`}
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, ease: EASE, delay: 0.25 + i * 0.12 }}
              >
                {l.t}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p {...fade(0.85)} className="mt-5 max-w-xl text-[15px] leading-relaxed text-ink-soft sm:mt-7 sm:text-lg" data-testid="hero-description">
          Entdecke ausgewählte Beauty- und Wellnessbehandlungen in einer stilvollen und persönlichen Atmosphäre. Ich freue mich darauf, dich bei Maison Princess willkommen zu heißen.
        </motion.p>

        <motion.div {...fade(1)} className="mt-6 flex flex-col gap-4 sm:mt-9 sm:flex-row sm:items-center sm:gap-7">
          <button
            data-testid="hero-booking-btn"
            onClick={() => scrollToId("termin")}
            className="btn-gold group inline-flex items-center justify-center gap-3 rounded-full px-9 py-4 text-[13px] font-semibold uppercase tracking-[0.26em]"
          >
            Termin anfragen
            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
          </button>
          <button
            data-testid="hero-location-link"
            onClick={() => scrollToId("kontakt")}
            className="inline-flex items-center gap-2 text-[13px] text-ink-soft hover:text-ink"
          >
            <MapPin className="h-4 w-4 text-gold-deep" />
            {STUDIO.street.replace("Straße", "Str.")} · Bensheim
          </button>
        </motion.div>
      </div>

      <HeroVisual />
    </div>
    <div className="mx-auto max-w-7xl px-5 pb-16 sm:px-8 lg:pb-24">
      <GiftNote />
    </div>
  </section>
);
